import React, { useContext, useEffect, useRef, useState, forwardRef } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    Autocomplete,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/styles/AddModal.scss";
import { CommonContext, MerchantWarehouseContext } from "../../context";
import commonApi from "../../utils/api";
import AlertMessage from "../common/AlertMessage";
import { scrollToTop } from "../common/scrollToTop";
import moment from "moment";

const CustomDateInput = forwardRef(({ value, onClick, label, error, helperText, placeholder }, ref) => (
    <TextField
        onClick={onClick}
        value={value}
        label={label}
        inputRef={ref}
        placeholder={placeholder}
        error={!!error}
        helperText={helperText}
        fullWidth
        size="small"
        InputProps={{
            readOnly: true,
        }}
    />
));

const initialFormState = {
    merchant_id: "",
    status: "1",
    start_date: null,
    area_per_sqft: "0",
    price_per_sqft: "0",
};

const AddModal = () => {
    const { modalAlert, setModalAlert, setAlertMessage, setSeverity, merchant, setIsLoading } =
        useContext(CommonContext);
    const {
        addModal,
        handleCloseModal,
        onLoadApi,
        viewData,
        isEdit,
        warehouseMasterId
    } = useContext(MerchantWarehouseContext);

    const [formDataTemp, setFormDataTemp] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState({});
    const modalContentRef = useRef(null);

    useEffect(() => {
        if (viewData && isEdit) {
            setFormDataTemp({
                id: viewData.id,
                merchant_id: viewData.merchant_id || "",
                status: viewData.status?.toString() || "1",
                start_date: viewData.start_date ? new Date(viewData.start_date) : null,
                area_per_sqft: viewData.area_per_sqft || "0",
                price_per_sqft: viewData.price_per_sqft || "0",
            });
        } else {
            setFormDataTemp(initialFormState);
        }
        setFormErrors({});
    }, [viewData, isEdit, addModal]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDataTemp((prev) => ({ ...prev, [name]: value }));
    };

    const validateSingle = (name, value) => {
        switch (name) {
            case "merchant_id":
                return value ? "" : "Merchant is required";
            case "status":
                return value !== "" ? "" : "Status is required";
            case "start_date":
                return value ? "" : "Start Date is required";
            case "area_per_sqft":
                return (!value || Number(value) <= 0) ? "Area must be greater than 0" : "";
            case "price_per_sqft":
                return (!value || Number(value) <= 0) ? "Price must be greater than 0" : "";
            default:
                return "";
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        const err = validateSingle(name, formDataTemp[name]);
        setFormErrors((prev) => ({ ...prev, [name]: err }));
    };

    const validateForm = () => {
        const errors = {};
        ["merchant_id", "status", "start_date", "area_per_sqft", "price_per_sqft"].forEach((key) => {
            const err = validateSingle(key, formDataTemp[key]);
            if (err) errors[key] = err;
        });
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);

        const payload = {
            ...formDataTemp,
            warehouse_master_id: warehouseMasterId,
            status: parseInt(formDataTemp.status),
            start_date: formDataTemp.start_date ? moment(formDataTemp.start_date).format("YYYY-MM-DD") : null,
            area_per_sqft: parseFloat(formDataTemp.area_per_sqft),
            price_per_sqft: parseFloat(formDataTemp.price_per_sqft),
        };

        const url = isEdit ? "/warehouse-master-merchant/edit" : "/warehouse-master-merchant/add";

        try {
            const res = await commonApi.post(url, payload);
            setModalAlert(true);
            scrollToTop(modalContentRef);

            if (res?.data?.s === 1) {
                setAlertMessage(res?.data?.msg || "Data saved successfully.");
                setSeverity("success");
                setTimeout(() => {
                    handleCloseModal();
                    onLoadApi();
                }, 1500);
            } else {
                setAlertMessage(res?.data?.msg || "Something went wrong.");
                setSeverity("error");
            }
        } catch (err) {
            scrollToTop(modalContentRef);
            setModalAlert(true);
            setAlertMessage(err?.response?.data?.msg || err?.message || "Error submitting form.");
            setSeverity("error");
            console.error("Error submitting form:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={addModal}>
            <Box className="add-modal">
                <Box className="modal-header">
                    <Typography variant="h6">
                        {isEdit ? "Edit" : "Assign"} Merchant to Warehouse
                    </Typography>
                    <IconButton onClick={handleCloseModal} size="small">
                        <Close />
                    </IconButton>
                </Box>

                <Box className="content-body" ref={modalContentRef}>
                    {modalAlert && <AlertMessage />}

                    <form className="custom-form" onSubmit={handleSubmit} noValidate>
                        <Card className="form-section">
                            <Typography variant="h6" className="header-title">Assignment Details</Typography>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <Autocomplete
                                            options={merchant || []}
                                            getOptionLabel={(option) => option.company_name}
                                            value={merchant?.find(item => item.id === formDataTemp.merchant_id) || null}
                                            onChange={(event, newValue) => {
                                                setFormDataTemp(prev => ({ ...prev, merchant_id: newValue ? newValue.id : "" }));
                                                setFormErrors(prev => ({ ...prev, merchant_id: "" }));
                                            }}
                                            onBlur={() => {
                                                const err = validateSingle("merchant_id", formDataTemp.merchant_id);
                                                setFormErrors(prev => ({ ...prev, merchant_id: err }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Merchant"
                                                    variant="outlined"
                                                    size="small"
                                                    error={!!formErrors.merchant_id}
                                                    helperText={formErrors.merchant_id}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <DatePicker
                                            selected={formDataTemp.start_date}
                                            onChange={(date) => {
                                                setFormDataTemp((prev) => ({
                                                    ...prev,
                                                    start_date: date,
                                                }))
                                                setFormErrors((prev) => ({ ...prev, start_date: "" }));
                                            }}
                                            dateFormat="dd-MM-yyyy"
                                            wrapperClassName="full-width-datepicker"
                                            customInput={
                                                <CustomDateInput
                                                    label="Select Start Date"
                                                    error={formErrors.start_date}
                                                    helperText={formErrors.start_date}
                                                />
                                            }
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Area (sqft)"
                                            name="area_per_sqft"
                                            type="number"
                                            value={formDataTemp.area_per_sqft}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.area_per_sqft}
                                            helperText={formErrors.area_per_sqft}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Price (sqft)"
                                            name="price_per_sqft"
                                            type="number"
                                            value={formDataTemp.price_per_sqft}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.price_per_sqft}
                                            helperText={formErrors.price_per_sqft}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 6 }}>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                name="status"
                                                value={formDataTemp.status}
                                                onChange={handleChange}
                                                row
                                            >
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio />}
                                                    label="Active"
                                                />
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio />}
                                                    label="Inactive"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </form>
                </Box>

                <Box className="form-actions">
                    <Button
                        variant="outlined"
                        onClick={handleCloseModal}
                        color="error"
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        type="submit"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddModal;
